import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import controller from "../../utils/controller"

export default class extends Controller {
  static targets = [
    "schedules",
    "scheduleTemplate",
    "destroy",
    "allTimes",
    "addScheduleButton",
    "addScheduleRow",
    "smartLockMessage",
    "smartLockScheduleModal",
    "attachableSelect",
  ]

  static outlets = [
    "schedule",
  ]

  static values = {
    selectedDevice: Object,
    smartLockCheckUrl: String,
    smartLock: Boolean,
  }

  connect() {
    if (this.schedulesTarget.children.length === 0) this.addSchedule()
    if (this.hasSelectedDeviceValue) this.setSelectedDevice()
    this.toggleRemoteLock()

    // delay until next tick so that schedule controller are connected
    setTimeout(() => { this.syncAllTimes() }, 0)
  }

  setSelectedDevice() {
    const select = this.attachableSelectTarget

    if (!select) return
    select.innerHTML = `<option value="${this.selectedDeviceValue.value}">${this.selectedDeviceValue.text}</option>`
    this.smartLockValue = Boolean(this.selectedDeviceValue.smart_lock)
  }
  addSchedule() {
    const content = this.scheduleTemplateTarget.innerHTML.replace(/NEW_SCHEDULE/g, new Date().getTime())
    this.schedulesTarget.insertAdjacentHTML("beforeend", content)
  }

  defaultAllTimes() {
    if (this.emptySchedule()) this.enableAllTimes()
  }

  syncAllTimes() {
    this.allTimesTarget.checked = this.allTimes()
    this.toggleAllTimes()
  }

  toggleAllTimes() {
    if (this.allTimesTarget.checked) {
      this.enableAllTimes()
    } else {
      this.disableAllTimes()
    }
  }

  disableAllTimes() {
    this.allTimesTarget.checked = false
    this.addScheduleButtonTarget.disabled = false
  }

  enableAllTimes() {
    this.allTimesTarget.checked = true
    if (!this.allTimes()) this.setAllTimes()
    this.addScheduleButtonTarget.disabled = true
  }

  setAllTimes() {
    this.scheduleOutlets.forEach(schedule => schedule.remove())
    this.addSchedule()
    // wait for next tick so that the new schedule controller gets connected
    setTimeout(() => { this.lastScheduleOutlet.setAllTimes() }, 0)
  }

  emptySchedule() {
    return this.scheduleOutlets.length === 1 && this.scheduleOutlets[0].empty()
  }

  allTimes() {
    const schedules = this.scheduleOutlets.filter(schedule => !schedule.removed())
    return schedules.length === 1 && schedules[0].allTimes()
  }

  handleLock() {
    this.toggleRemoteLock()
    this.checkSmartLock()
  }

  toggleRemoteLock() {
    const selectedDeviceText = this.attachableSelectTarget.options[this.attachableSelectTarget.selectedIndex].text

    if (selectedDeviceText.includes("RemoteLock")) {
      this.disableAccessTimes()
    } else {
      this.enableAccessTimes()
    }
  }

  async checkSmartLock() {
    const gid = this.attachableSelectTarget.value

    if (!gid || !this.hasSmartLockCheckUrlValue) {
      this.smartLockValue = false
      return
    }

    try {
      const response = await post(this.smartLockCheckUrlValue, { body: { gid } })
      const data = await response.json
      this.smartLockValue = data.smart_lock
    } catch {
      this.smartLockValue = false
    }
  }

  smartLockValueChanged(isSmartLock) {
    if (this.hasAddScheduleRowTarget) this.addScheduleRowTarget.classList.toggle("hidden", isSmartLock)
    if (this.hasSmartLockMessageTarget) this.smartLockMessageTarget.classList.toggle("hidden", !isSmartLock)
  }

  hasSmartLockScheduleViolation() {
    if (!this.smartLockValue) return false

    const schedules = this.scheduleOutlets.filter(schedule => !schedule.removed())
    return schedules.length > 1
  }

  showSmartLockScheduleModal() {
    controller(this.smartLockScheduleModalTarget, "modal").open()
  }

  disableAccessTimes() {
    if (this.allTimesTarget.disabled) return

    this.enableAllTimes()
    this.allTimesTarget.disabled = true
    this.lastScheduleOutlet.disable()
    this.setOpacity(0.5)
  }

  enableAccessTimes() {
    if (!this.allTimesTarget.disabled) return

    this.disableAllTimes()
    this.allTimesTarget.disabled = false
    this.lastScheduleOutlet.enable()
    this.setOpacity(1)
  }

  setOpacity(value) {
    const elements = [this.schedulesTarget, this.allTimesTarget.parentElement, this.addScheduleButtonTarget]
    elements.forEach(element => element.style.opacity = value)
  }

  remove(event) {
    // using the ":prevent" action option changes the order of actions:
    // https://github.com/hotwired/stimulus/issues/718
    event?.preventDefault()

    this.destroyTarget.value = "1"
    this.element.classList.add("hidden")
  }

  get lastScheduleOutlet() {
    return this.scheduleOutlets[this.scheduleOutlets.length - 1]
  }
}
